import { useEffect, useMemo, useRef, useState } from "react";
import ExcelJS from "exceljs";
import { Download, Upload, FileText, AlertCircle, CheckCircle, Pencil, Check, X, ChevronLeft, ChevronRight, Trash2, Printer, Settings, Loader2, Plus } from "lucide-react";
import { toast } from "react-toastify";

import { useTranslation } from "../i18n";
import { useUrlSearchParam } from "../hooks/useUrlSearchParam";
import FilterPills from "./admin/FilterPills";
import {
    buildLocationLabel,
    downloadLocationLabelPdf,
    silentPrint,
    browserPrint,
    LOCATION_LABEL_SIZES,
    RACKING_LABEL_BASE,
    type LocationLabelSize,
} from "./helpers/printAgent";
import {
    submitNetworkPrintJob,
    getSavedNetworkPrinter,
} from "./helpers/networkPrint";
import PrinterSelectModal from "./PrinterSelectModal";
import { isAdminPosition } from "../access/positions";
import SortingLabelPrintModal from "./SortingLabelPrintModal";
import {
    DEFAULT_LABEL_PALETTE, isRackingLocation, printWarehouse, rackingLabel, readRackingLabelSize,
    type LabelPalette,
} from "./helpers/rackingLabels";
import { apiFetch } from "../api/apiFetch";
import { useReadOnly } from "../access/ReadOnly";
import { useWarehouseScope } from "../access/useWarehouseScope";

// Pull signed-in user identity for audit-log attribution. Spread into
// any mutating request body so the auto-logging middleware records who
// performed the action (otherwise these admin bulk edits land
// anonymous because session.user is bypassed).
const readActor = (): { uid?: number; name?: string; warehouse?: string } => {
    try {
        const raw = window.localStorage.getItem("user");
        if (!raw) return {};
        const u = JSON.parse(raw);
        const out: { uid?: number; name?: string; warehouse?: string } = {};
        if (typeof u?.uid === "number") out.uid = u.uid;
        if (typeof u?.name === "string") out.name = u.name;
        if (typeof u?.warehouse === "string") out.warehouse = u.warehouse;
        return out;
    } catch { return {}; }
};

// Label-size preference is sticky across reloads — admins shouldn't have
// to re-pick A4 every time they re-open the locations page. Keyed only
// for this admin page so it's independent of any other label-size pref
// elsewhere in the app.
const LOCATION_LABEL_SIZE_KEY = "locations-label-size";
const readLocationLabelSize = (): LocationLabelSize => {
    try {
        const v = window.localStorage.getItem(LOCATION_LABEL_SIZE_KEY);
        if (v && (LOCATION_LABEL_SIZES as readonly string[]).includes(v)) {
            return v as LocationLabelSize;
        }
    } catch { /* fall through */ }
    return "a5";
};
const writeLocationLabelSize = (size: LocationLabelSize) => {
    try { window.localStorage.setItem(LOCATION_LABEL_SIZE_KEY, size); } catch { /* ignore */ }
};
// Compact label shown on the cycle button — thermal sizes by their short
// side in mm, paper sizes by their standard name.
const LOCATION_LABEL_DISPLAY: Record<LocationLabelSize, string> = {
    scansheet: "65mm",
    dispatch: "100mm",
    a5: "A5",
    a4: "A4",
};

interface LocationRow {
    barcode: string;
    warehouse: string;
    type: string | null;
    zone: string | null;
    aisle: string | null;
    bay: string | null;
    level: string | null;
    display_name: string | null;
    walk_order: number | null;
    size: string | null;
    // "high" / "low" — vertical position within the rack. Free of the rest
    // of the location hierarchy (zone/aisle/bay/level): a level-3 bay can
    // still be a "low" pick if the rack is short.
    high_low: "high" | "low" | null;
    // 1-10 scale; 1 = cold, 10 = hot.
    temperature: number | null;
    // "sellable" → stock here is synced to sales channels.
    // "unsellable" → stock here is excluded from the sync.
    // NOT NULL on the DB side (defaults to "sellable") so this is always set.
    sellable: "sellable" | "unsellable";
    // Whether this location is an eligible *source* for Shelf Drop /
    // Forecast Drop replenishment. True for high reserve locations that
    // pickers walk to and pull stock down from. NOT NULL (defaults to
    // FALSE).
    shelf_drop: boolean;
    // "A1".."E6" — slot position on the bay diagram shown on the
    // unified picker screen for low locations. NULL means "no diagram"
    // (picker doesn't need help finding the slot — usually only
    // floor-level shelves benefit from the visual).
    bay_position: string | null;
    created_at?: string;
}

interface BulkResult {
    added: number;
    updated: number;
    removed: number;
    errors: { row: number; reason: string }[];
}

const CHUNK_SIZE = 500;

// Display cap for the Barcode and Name columns. Anything longer is
// rendered as the first 12 chars + "…" so the row stays tight enough
// to fit the desktop viewport without horizontal scroll. The full
// value is exposed via the cell's title attribute for hover/long-press.
const LOC_CELL_MAX_CHARS = 13;
const truncateCell = (s: string): string =>
    s.length > LOC_CELL_MAX_CHARS ? s.slice(0, LOC_CELL_MAX_CHARS - 1) + "…" : s;

// Identifier columns that round-trip as Text — barcode is the location's
// scannable PK, and zone/aisle/bay/level can be single-letter or
// single-digit codes that Excel would otherwise auto-coerce to numbers
// (and thereby strip a leading zero). walk_order/temperature stay
// numeric so admins can sort/filter on them in Excel.
const TEXT_COLUMNS = new Set<string>([
    "barcode", "zone", "aisle", "bay", "level",
]);

const cellToString = (cell: ExcelJS.Cell | undefined): string => {
    if (!cell) return "";
    const v = cell.value;
    if (v === null || v === undefined) return "";
    if (typeof v === "string") return v;
    if (typeof v === "number" || typeof v === "boolean") return String(v);
    if (v instanceof Date) return v.toISOString().slice(0, 10);
    if (typeof v === "object") {
        if ("result" in v && (v as { result?: unknown }).result != null) {
            return String((v as { result: unknown }).result);
        }
        if ("text" in v && (v as { text?: unknown }).text != null) {
            return String((v as { text: unknown }).text);
        }
        if ("richText" in v) {
            const rt = (v as { richText?: { text: string }[] }).richText;
            return Array.isArray(rt) ? rt.map(r => r.text).join("") : "";
        }
    }
    return String(v);
};

const downloadXlsx = async (filename: string, header: readonly string[], rows: string[][]) => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Locations");
    ws.addRow(header as string[]);
    for (const r of rows) ws.addRow(r);
    header.forEach((name, i) => {
        if (TEXT_COLUMNS.has(name)) ws.getColumn(i + 1).numFmt = "@";
    });
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

const readXlsxRows = async (file: File): Promise<{ header: string[]; rows: string[][] }> => {
    const buffer = await file.arrayBuffer();
    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(buffer);
    const ws = wb.worksheets[0];
    if (!ws || ws.rowCount < 1) return { header: [], rows: [] };
    const headerRow = ws.getRow(1);
    const colCount = headerRow.cellCount;
    const header: string[] = [];
    for (let c = 1; c <= colCount; c++) {
        header[c - 1] = cellToString(headerRow.getCell(c)).trim().toLowerCase();
    }
    const rows: string[][] = [];
    for (let r = 2; r <= ws.rowCount; r++) {
        const xrow = ws.getRow(r);
        const cells: string[] = [];
        for (let c = 1; c <= colCount; c++) cells[c - 1] = cellToString(xrow.getCell(c));
        if (cells.some(s => s.trim() !== "")) rows.push(cells);
    }
    return { header, rows };
};

// action, warehouse, type, zone, aisle, bay, level, walk_order,
// size, high_low, temperature, barcode, name, sellable
// barcode is the PK (the scannable ID) — required on add/update/remove.
// type ∈ admin-managed catalogue (Settings > Locations > Types);
// seeded with storage / lane / area / pallet. Defaults to 'storage'
// when blank on add. Round-trips through download/upload. The
// legacy values 'loading-bay' / 'inbound-processing' are still
// accepted as aliases on import for older CSVs.
// high_low ∈ { high, low }. temperature is 1-10 (1=cold, 10=hot).
// sellable ∈ { sellable, unsellable }; defaults to 'sellable' when
// blank on add (matches the DB default).
// shelf_drop: boolean — true / false. Blank on add → false; blank
// on update → leave unchanged.
const HEADER = [
    "action", "warehouse", "type",
    "zone", "aisle", "bay", "level",
    "walk_order",
    "size", "high_low", "temperature",
    "barcode", "name", "sellable", "shelf_drop", "bay_position",
];

const LocationsAdmin = () => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const [locations, setLocations] = useState<LocationRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
    const [lastResult, setLastResult] = useState<BulkResult | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const refresh = async () => {
        try {
            const res = await apiFetch(`/put-away/locations`, {});
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data: LocationRow[] = await res.json();
            setLocations(data);
        } catch {
            toast.error(t("server_error"), { toastId: "locations-load-err" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { refresh(); }, []);

    const downloadTemplate = async () => {
        const rows: string[][] = [
            // Examples across all three warehouses + every supported type.
            // bay_position (last col) is "A1".."E6" — only meaningful for
            // low storage locations; blank elsewhere.
            ["add", "Newport",        "storage",  "A", "1", "1", "1", "10", "Small",  "low",  "5",  "NP-A-1-1-1",  "Bay 1 Level 1",        "sellable",   "false", "A1"],
            ["add", "Newport",        "lane",     "",  "",  "",  "",  "",   "",       "",     "",   "LB-A01",      "Lane A01",             "unsellable", "false", ""],
            ["add", "Newport",        "area",     "",  "",  "",  "",  "",   "",       "",     "",   "INBOUND-NPT", "Newport Inbound",      "unsellable", "false", ""],
            ["add", "Newport",        "pallet",   "",  "",  "",  "",  "",   "",       "",     "",   "NP-PAL-001",  "Pallet 001",           "sellable",   "false", ""],
            ["add", "Pembroke",       "storage",  "B", "2", "1", "1", "20", "Medium", "high", "3",  "PD-B-2-1-1",  "PD Bay 1 — Level 1",   "sellable",   "true",  ""],
            ["add", "Poland",         "storage",  "C", "3", "2", "1", "30", "Large",  "low",  "8",  "PL-C-3-2-1",  "Poland Bay 2 Level 1", "sellable",   "false", "B3"],
            ["update", "",            "",         "",  "",  "",  "",  "",   "",       "",     "",   "NP-A-1-1-1",  "Bay 1 — Level 1",      "",           "",      ""],
            ["remove", "",            "",         "",  "",  "",  "",  "",   "",       "",     "",   "NP-OLD-99",   "",                     "",           "",      ""],
        ];
        await downloadXlsx("locations-template.xlsx", HEADER, rows);
    };

    const downloadCurrent = async () => {
        const rows: string[][] = [];
        for (const l of locations) {
            rows.push([
                "",
                l.warehouse ?? "",
                l.type ?? "",
                l.zone ?? "",
                l.aisle ?? "",
                l.bay ?? "",
                l.level ?? "",
                l.walk_order != null ? String(l.walk_order) : "",
                l.size ?? "",
                l.high_low ?? "",
                l.temperature != null ? String(l.temperature) : "",
                l.barcode,
                l.display_name ?? "",
                l.sellable ?? "sellable",
                l.shelf_drop ? "true" : "false",
                l.bay_position ?? "",
            ]);
        }
        await downloadXlsx("locations-current.xlsx", HEADER, rows);
    };

    const onFileChosen = async (ev: React.ChangeEvent<HTMLInputElement>) => {
        const file = ev.target.files?.[0];
        if (!file) return;
        ev.target.value = "";

        let parsed: { header: string[]; rows: string[][] };
        try {
            parsed = await readXlsxRows(file);
        } catch {
            toast.error(t("locations_upload_parse_error"), { toastId: "locations-parse" });
            return;
        }
        const { header, rows: dataRows } = parsed;
        if (dataRows.length === 0) {
            toast.error(t("locations_upload_empty"), { toastId: "locations-empty" });
            return;
        }
        const actionIdx = header.indexOf("action");
        const barcodeIdx = header.indexOf("barcode");
        const warehouseIdx = header.indexOf("warehouse");
        const zoneIdx = header.indexOf("zone");
        const aisleIdx = header.indexOf("aisle");
        const bayIdx = header.indexOf("bay");
        const levelIdx = header.indexOf("level");
        // `name` is the current header; `display_name` is accepted for
        // CSVs exported before the column was renamed.
        const displayIdx = (() => {
            const i = header.indexOf("name");
            return i >= 0 ? i : header.indexOf("display_name");
        })();
        const walkOrderIdx = header.indexOf("walk_order");
        const sizeIdx = header.indexOf("size");
        const highLowIdx = header.indexOf("high_low");
        const temperatureIdx = header.indexOf("temperature");
        const typeIdx = header.indexOf("type");
        const sellableIdx = header.indexOf("sellable");
        const shelfDropIdx = header.indexOf("shelf_drop");
        const bayPositionIdx = header.indexOf("bay_position");
        if (actionIdx === -1 || barcodeIdx === -1) {
            toast.error(t("locations_upload_bad_header"), { toastId: "locations-hdr" });
            return;
        }

        const rows = dataRows.map(r => ({
            action: r[actionIdx] ?? "",
            barcode: r[barcodeIdx] ?? "",
            warehouse: warehouseIdx >= 0 ? (r[warehouseIdx] ?? "") : "",
            type: typeIdx >= 0 ? (r[typeIdx] ?? "") : "",
            zone: zoneIdx >= 0 ? (r[zoneIdx] ?? "") : "",
            aisle: aisleIdx >= 0 ? (r[aisleIdx] ?? "") : "",
            bay: bayIdx >= 0 ? (r[bayIdx] ?? "") : "",
            level: levelIdx >= 0 ? (r[levelIdx] ?? "") : "",
            display_name: displayIdx >= 0 ? (r[displayIdx] ?? "") : "",
            walk_order: walkOrderIdx >= 0 ? (r[walkOrderIdx] ?? "") : "",
            size: sizeIdx >= 0 ? (r[sizeIdx] ?? "") : "",
            high_low: highLowIdx >= 0 ? (r[highLowIdx] ?? "") : "",
            temperature: temperatureIdx >= 0 ? (r[temperatureIdx] ?? "") : "",
            sellable: sellableIdx >= 0 ? (r[sellableIdx] ?? "") : "",
            shelf_drop: shelfDropIdx >= 0 ? (r[shelfDropIdx] ?? "") : "",
            bay_position: bayPositionIdx >= 0 ? (r[bayPositionIdx] ?? "") : "",
        }));

        setUploading(true);
        setProgress({ done: 0, total: rows.length });
        setLastResult(null);

        const combined: BulkResult = { added: 0, updated: 0, removed: 0, errors: [] };
        try {
            for (let i = 0; i < rows.length; i += CHUNK_SIZE) {
                const chunk = rows.slice(i, i + CHUNK_SIZE);
                const res = await apiFetch(`/put-away/locations/bulk`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ rows: chunk, ...readActor() }),
                });
                // 413 = chunk body exceeds the BE's JSON limit. Stop the
                // upload (subsequent chunks will fail the same way) and
                // surface a specific message so the operator knows it's
                // a size issue, not a generic server fault.
                if (res.status === 413) {
                    toast.error(t("bulk_upload_too_large"), { toastId: "locations-upload-413" });
                    break;
                }
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const chunkResult: BulkResult = await res.json();

                combined.added += chunkResult.added;
                combined.updated += chunkResult.updated;
                combined.removed += chunkResult.removed;
                for (const e of chunkResult.errors) {
                    combined.errors.push({ row: e.row + i, reason: e.reason });
                }
                setProgress({ done: Math.min(i + chunk.length, rows.length), total: rows.length });
            }
            setLastResult(combined);
            await refresh();
        } catch {
            toast.error(t("server_error"), { toastId: "locations-upload-err" });
        } finally {
            setUploading(false);
            setProgress(null);
        }
    };

    return (
        <div className="totes-admin">
            <div className="totes-admin-actions">
                <button type="button" className="btn btn-ghost" onClick={downloadTemplate}>
                    <FileText size={16} /> {t("totes_download_template")}
                </button>
                <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={downloadCurrent}
                    disabled={loading || locations.length === 0}
                >
                    <Download size={16} /> {t("totes_download_current")}
                </button>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading || readOnly}
                    aria-disabled={readOnly}
                    title={readOnly ? t("read_only_mode") : undefined}
                >
                    <Upload size={16} /> {uploading ? t("submitting") : t("locations_upload_xlsx")}
                </button>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    style={{ display: "none" }}
                    onChange={onFileChosen}
                />
            </div>

            {progress && (
                <div className="totes-admin-progress">
                    <div className="totes-admin-progress-label">
                        {t("totes_upload_progress", {
                            done: String(progress.done),
                            total: String(progress.total),
                        })}
                        <span className="totes-admin-progress-pct">
                            {Math.round((progress.done / Math.max(progress.total, 1)) * 100)}%
                        </span>
                    </div>
                    <div className="totes-admin-progress-track">
                        <div
                            className="totes-admin-progress-fill"
                            style={{ width: `${(progress.done / Math.max(progress.total, 1)) * 100}%` }}
                        />
                    </div>
                </div>
            )}

            {lastResult && (
                <div className="totes-admin-result">
                    <div className="totes-admin-result-line">
                        <CheckCircle strokeWidth={3} size={16} className="totes-admin-result-ok" />
                        <span>
                            {t("totes_bulk_summary", {
                                added: String(lastResult.added),
                                updated: String(lastResult.updated),
                                removed: String(lastResult.removed),
                            })}
                        </span>
                    </div>
                    {lastResult.errors.length > 0 && (
                        <ul className="totes-admin-errors">
                            {lastResult.errors.map((e, i) => (
                                <li key={i}>
                                    <AlertCircle size={14} />
                                    <span>{t("totes_row_label", { row: String(e.row) })}: {e.reason}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            {!loading && <LocationsSummary locations={locations} />}
            {!loading && (
                <LocationsTable
                    locations={locations}
                    onUpdated={(barcode, patch) =>
                        setLocations(curr => curr.map(l => l.barcode === barcode ? { ...l, ...patch } : l))
                    }
                    onDeleted={(barcode) =>
                        setLocations(curr => curr.filter(l => l.barcode !== barcode))
                    }
                    onAdded={(row) => setLocations(curr => [row, ...curr])}
                />
            )}
        </div>
    );
};

const LOCATIONS_PAGE_SIZE = 25;

// Type values come from the admin-managed location_types catalogue
// (Settings > Locations > Types). The previous hard-coded list moved
// to the DB in migration 032 so adding a new type doesn't require a
// code change. Legacy DB rows might still come back as "loading-bay"
// or "inbound-processing" until the rename migration has been run;
// the union below surfaces those so admins can re-pick the new value.
const LOCATION_SELLABLE = ["sellable", "unsellable"] as const;
const LOCATION_HIGH_LOW = ["high", "low"] as const;
// The print dialog offers one tick box that leaves these racking levels out
// of a run (Alex, 2026-10-15: print the low locations without L4 and L5).
const RACKING_SKIP_LEVELS = [4, 5];
// Temperature is a 1-10 integer scale (1 = cold, 10 = hot). Numeric input
// rather than enum so admins aren't forced through a 10-option dropdown.
const LOCATION_TEMP_MIN = 1;
const LOCATION_TEMP_MAX = 10;

// Slot options for the bay diagram. Mirrors the DB CHECK constraint
// (locations_bay_position_format): letter A–E, digit 1–6.
const LOCATION_BAY_POSITIONS: string[] = [];
for (const row of ["A", "B", "C", "D", "E"]) {
    for (let col = 1; col <= 6; col++) LOCATION_BAY_POSITIONS.push(`${row}${col}`);
}

type LocationDraft = {
    warehouse: string;
    type: string;
    zone: string;
    aisle: string;
    bay: string;
    level: string;
    display_name: string;
    walk_order: string;
    size: string;
    high_low: string;
    temperature: string;
    sellable: string;
    shelf_drop: boolean;
    bay_position: string;
    barcode: string;
};

const emptyDraft = (l: LocationRow): LocationDraft => ({
    warehouse: l.warehouse ?? "",
    type: l.type ?? "",
    zone: l.zone ?? "",
    aisle: l.aisle ?? "",
    bay: l.bay ?? "",
    level: l.level ?? "",
    display_name: l.display_name ?? "",
    walk_order: l.walk_order != null ? String(l.walk_order) : "",
    size: l.size ?? "",
    high_low: l.high_low ?? "",
    temperature: l.temperature != null ? String(l.temperature) : "",
    sellable: l.sellable ?? "sellable",
    shelf_drop: !!l.shelf_drop,
    bay_position: l.bay_position ?? "",
    barcode: l.barcode,
});

const LocationsTable = ({
    locations,
    onUpdated,
    onDeleted,
    onAdded,
}: {
    locations: LocationRow[];
    onUpdated: (barcode: string, patch: Partial<LocationRow>) => void;
    onDeleted: (barcode: string) => void;
    onAdded: (row: LocationRow) => void;
}) => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const [search, setSearch] = useUrlSearchParam("search");
    // Local input state — only commits to `search` on Enter / blur, so a
    // fast typist doesn't fire a re-filter per keystroke.
    const [searchInput, setSearchInput] = useState(search);
    useEffect(() => { setSearchInput(search); }, [search]);
    const warehouseScope = useWarehouseScope();
    const [pickedWarehouse, setWarehouseFilter] = useState("");
    // Without cross_warehouse the table is pinned to the person's own warehouse.
    const warehouseFilter = !warehouseScope.canCross && warehouseScope.own ? warehouseScope.own : pickedWarehouse;
    const [typeFilter, setTypeFilter] = useState("");
    const [sizeFilter, setSizeFilter] = useState("");
    const [highLowFilter, setHighLowFilter] = useState("");
    const [page, setPage] = useState(0);
    // Configured size catalogue (Settings > Locations). Drives the Size
    // dropdowns in both the filters row and the per-row edit form.
    // Existing locations may carry sizes that are no longer in the
    // catalogue; those are merged in below so the dropdown still
    // surfaces them.
    const [sizes, setSizes] = useState<string[]>([]);
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(`/put-away/location-sizes`, {});
                if (!res.ok) return;
                const data: { name: string }[] = await res.json();
                if (alive) setSizes(data.map(d => d.name));
            } catch { /* silent — sizes degrade to free-text */ }
        })();
        return () => { alive = false; };
    }, []);
    // Configured types catalogue (Settings > Locations > Types). Same
    // shape as sizes: union with values already present on locations
    // so legacy/orphaned types still surface in the edit dropdown.
    const [catalogueTypes, setCatalogueTypes] = useState<string[]>([]);
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(`/put-away/location-types`, {});
                if (!res.ok) return;
                const data: { name: string }[] = await res.json();
                if (alive) setCatalogueTypes(data.map(d => d.name));
            } catch { /* silent — empty list falls back to whatever's on existing rows */ }
        })();
        return () => { alive = false; };
    }, []);
    // Barcode renames are Admin-only (the BE enforces it too — this
    // just decides whether the popup's barcode box is editable).
    const isSuperAdmin = useMemo(() => {
        try {
            const raw = window.localStorage.getItem("user");
            if (!raw) return false;
            const u = JSON.parse(raw);
            return isAdminPosition(u?.position ?? u?.permissions);
        } catch {
            return false;
        }
    }, []);
    const [editingBarcode, setEditingBarcode] = useState<string | null>(null);
    // true while the popup is creating a new row (editingBarcode stays
    // null). Shares `draft` with edit mode so the field markup is one
    // block; saving goes through the bulk "add" action, which also
    // generates a barcode when the box is left blank.
    const [adding, setAdding] = useState(false);
    const [draft, setDraft] = useState<LocationDraft | null>(null);
    const [savingBarcode, setSavingBarcode] = useState<string | null>(null);
    // Soft-delete confirmation: target row pinned in state so the modal
    // has the full row context (warehouse, display name) without prop
    // drilling. null = modal hidden.
    const [pendingDelete, setPendingDelete] = useState<LocationRow | null>(null);
    const [deletingBarcode, setDeletingBarcode] = useState<string | null>(null);

    // Print-label state. Mirrors DesksAdmin/BoxesAdmin: per-row spinner,
    // a "needs setup" hint on the printer-select button if a print just
    // failed, and a printer modal copy.
    const [printingBarcode, setPrintingBarcode] = useState<string | null>(null);
    const [downloadingBarcode, setDownloadingBarcode] = useState<string | null>(null);
    const [printerNeedsSetup, setPrinterNeedsSetup] = useState(false);
    const [showPrinterSelect, setShowPrinterSelect] = useState(false);
    const [labelSize, setLabelSize] = useState<LocationLabelSize>(() => readLocationLabelSize());
    useEffect(() => { writeLocationLabelSize(labelSize); }, [labelSize]);
    const cycleLabelSize = () => {
        setLabelSize(prev => {
            const idx = LOCATION_LABEL_SIZES.indexOf(prev);
            return LOCATION_LABEL_SIZES[(idx + 1) % LOCATION_LABEL_SIZES.length];
        });
    };

    // Bulk racking-label print — the Settings › Sorting bay print dialog,
    // reused for the high/low racking. It prints every racking location in
    // the current filter (lanes/areas/pallets are left to the per-row print)
    // for ONE warehouse, because the stock size it saves is per warehouse.
    const [printRacking, setPrintRacking] = useState(false);
    const [rackingSize, setRackingSize] = useState<{ w: number | null; h: number | null }>({ w: null, h: null });
    // Slot colours shared with the sorting labels and the picker's bay
    // diagram, loaded read-only (edited on Settings › Sorting).
    const [rackingPalette, setRackingPalette] = useState<LabelPalette>(DEFAULT_LABEL_PALETTE);
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(`/pick-and-sort-admin/colours`, {});
                if (!res.ok || !alive) return;
                const data = await res.json();
                if (alive && data?.shelf && data?.position) setRackingPalette({ shelf: data.shelf, position: data.position });
            } catch { /* keep defaults */ }
        })();
        return () => { alive = false; };
    }, []);

    const allWarehouses = useMemo(
        () => Array.from(new Set(locations.map(l => l.warehouse).filter(Boolean))).sort(),
        [locations],
    );
    const warehouses = useMemo(() => warehouseScope.limit(allWarehouses), [warehouseScope, allWarehouses]);
    const types = useMemo(
        () => Array.from(new Set(locations.map(l => l.type ?? "").filter(Boolean))).sort(),
        [locations],
    );
    // Size dropdown options = configured catalogue ∪ values already on
    // locations. The union surfaces any orphaned size labels (left over
    // after the catalogue entry was removed) so admins can still filter
    // or edit those rows back to a current value.
    const sizeOptions = useMemo(() => {
        const set = new Set<string>(sizes);
        for (const l of locations) if (l.size) set.add(l.size);
        return Array.from(set).sort();
    }, [sizes, locations]);
    // Type dropdown options for the edit form follow the same union
    // rule as sizes: catalogue values plus anything already on a
    // location, so a legacy/orphaned type can still be re-selected.
    const typeOptions = useMemo(() => {
        const set = new Set<string>(catalogueTypes);
        for (const l of locations) if (l.type) set.add(l.type);
        return Array.from(set).sort();
    }, [catalogueTypes, locations]);

    const filtered = useMemo(() => {
        // Trailing '*' switches the search to exact (case-insensitive)
        // match on the Name column only — mirrors the totes search
        // behaviour. Substring searches without '*' still span every
        // identifier column so admins can find rows by partial barcode,
        // zone code, etc.
        const raw = search.trim();
        const exact = raw.endsWith("*");
        const q = (exact ? raw.slice(0, -1) : raw).toLowerCase();
        return locations.filter(l => {
            if (warehouseFilter && l.warehouse !== warehouseFilter) return false;
            if (typeFilter && (l.type ?? "") !== typeFilter) return false;
            if (sizeFilter && (l.size ?? "") !== sizeFilter) return false;
            if (highLowFilter && (l.high_low ?? "") !== highLowFilter) return false;
            if (!q) return true;
            if (exact) {
                return l.display_name != null && l.display_name.toLowerCase() === q;
            }
            return [l.barcode, l.warehouse, l.type, l.zone, l.aisle, l.bay, l.level, l.display_name, l.size, l.high_low]
                .some(v => v != null && String(v).toLowerCase().includes(q));
        });
    }, [locations, search, warehouseFilter, typeFilter, sizeFilter, highLowFilter]);

    const rackingRows = useMemo(() => filtered.filter(isRackingLocation), [filtered]);
    const rackingWarehouse = printWarehouse(rackingRows, warehouseFilter);
    const openRackingPrint = async () => {
        if (!rackingWarehouse) return;
        // Start from the built-in size so a failed read can't print at a
        // size that belongs to a different warehouse.
        setRackingSize({ w: null, h: null });
        try {
            const res = await apiFetch(`/settings/${encodeURIComponent(rackingWarehouse)}`, {});
            if (res.ok) setRackingSize(readRackingLabelSize(await res.json()));
        } catch { /* keep the built-in default */ }
        setPrintRacking(true);
    };
    // Its own route (2026-10-20): anyone who can open Locations may save the
    // racking label size; the general settings PATCH is Admin-only.
    const saveRackingSize = async (w: number | null, h: number | null): Promise<boolean | "forbidden"> => {
        if (!rackingWarehouse) return false;
        try {
            const res = await apiFetch(`/settings/${encodeURIComponent(rackingWarehouse)}/racking-label-size`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ racking_label_width_mm: w, racking_label_height_mm: h }),
            });
            if (res.status === 403) return "forbidden";
            if (!res.ok) return false;
            setRackingSize(readRackingLabelSize(await res.json()));
            return true;
        } catch { return false; }
    };

    // Hidden when no filter is active — the full-dataset export is
    // already available via "Download Current" in the parent toolbar.
    const hasActiveFilter =
        search.trim() !== "" ||
        warehouseFilter !== "" ||
        typeFilter !== "" ||
        sizeFilter !== "" ||
        highLowFilter !== "";

    const downloadFiltered = async () => {
        const rows: string[][] = filtered.map(l => [
            "",
            l.warehouse ?? "",
            l.type ?? "",
            l.zone ?? "",
            l.aisle ?? "",
            l.bay ?? "",
            l.level ?? "",
            l.walk_order != null ? String(l.walk_order) : "",
            l.size ?? "",
            l.high_low ?? "",
            l.temperature != null ? String(l.temperature) : "",
            l.barcode,
            l.display_name ?? "",
            l.sellable ?? "sellable",
        ]);
        await downloadXlsx("locations-filtered.xlsx", HEADER, rows);
    };

    // Reset to the first page when filters change so users always start
    // from the top of a fresh result set rather than peering at page 3
    // of nothing.
    useEffect(() => {
        setPage(0);
    }, [search, warehouseFilter, typeFilter, sizeFilter, highLowFilter]);

    const startEdit = (l: LocationRow) => {
        if (readOnly) return;
        setEditingBarcode(l.barcode);
        setDraft(emptyDraft(l));
    };

    const cancelEdit = () => {
        setEditingBarcode(null);
        setAdding(false);
        setDraft(null);
    };

    // Sensible defaults for a fresh row: the warehouse currently filtered
    // on (or the first known one), type "storage" when the catalogue has
    // it, sellable, no shelf drop.
    const startAdd = () => {
        setEditingBarcode(null);
        setAdding(true);
        setDraft({
            warehouse: warehouseFilter || warehouses[0] || "",
            type: typeOptions.includes("storage") ? "storage" : (typeOptions[0] ?? ""),
            zone: "",
            aisle: "",
            bay: "",
            level: "",
            display_name: "",
            walk_order: "",
            size: "",
            high_low: "",
            temperature: "",
            sellable: "sellable",
            shelf_drop: false,
            bay_position: "",
            barcode: "",
        });
    };

    const ADD_SAVING_KEY = "__new__";
    const saveAdd = async () => {
        if (!adding || !draft) return;
        const norm = (s: string): string | null => {
            const v = s.trim();
            return v === "" ? null : v;
        };
        const warehouse = norm(draft.warehouse);
        const type = norm(draft.type);
        if (!warehouse) {
            toast.error(t("locations_edit_required", { field: t("warehouse_col") }), { toastId: "loc-add" });
            return;
        }
        if (!type) {
            toast.error(t("locations_edit_required", { field: t("locations_col_type") }), { toastId: "loc-add" });
            return;
        }
        const numOrNull = (raw: string): number | null => {
            const s = raw.trim();
            if (s === "") return null;
            const n = Number(s);
            return Number.isFinite(n) ? Math.round(n) : null;
        };
        const walkOrder = numOrNull(draft.walk_order);
        let temperature = numOrNull(draft.temperature);
        if (temperature !== null) {
            temperature = Math.max(LOCATION_TEMP_MIN, Math.min(LOCATION_TEMP_MAX, temperature));
        }
        const bayPosition = norm(draft.bay_position)?.toUpperCase() ?? null;
        // The bulk endpoint treats blank as "not provided" on add, which
        // is exactly what a fresh form wants — DB defaults fill the rest.
        const row = {
            action: "add",
            barcode: draft.barcode.trim(),
            warehouse,
            type,
            zone: norm(draft.zone) ?? "",
            aisle: norm(draft.aisle) ?? "",
            bay: norm(draft.bay) ?? "",
            level: norm(draft.level) ?? "",
            display_name: norm(draft.display_name) ?? "",
            walk_order: walkOrder ?? "",
            size: norm(draft.size) ?? "",
            high_low: norm(draft.high_low) ?? "",
            temperature: temperature ?? "",
            sellable: norm(draft.sellable) ?? "sellable",
            shelf_drop: Boolean(draft.shelf_drop),
            bay_position: bayPosition ?? "",
        };
        setSavingBarcode(ADD_SAVING_KEY);
        try {
            const res = await apiFetch(`/put-away/locations/bulk`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ rows: [row], ...readActor() }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const result: BulkResult & { generated?: { row: number; barcode: string }[] } = await res.json();
            if (result.errors.length > 0) {
                toast.error(result.errors[0].reason, { toastId: "loc-add" });
                return;
            }
            const barcode = row.barcode || result.generated?.[0]?.barcode || "";
            if (!barcode) {
                // Shouldn't happen (add always reports the barcode it used)
                // but don't fabricate a row the table can't key on.
                toast.error(t("server_error"), { toastId: "loc-add" });
                return;
            }
            onAdded({
                barcode,
                warehouse,
                type,
                zone: norm(draft.zone),
                aisle: norm(draft.aisle),
                bay: norm(draft.bay),
                level: norm(draft.level),
                display_name: norm(draft.display_name),
                walk_order: walkOrder,
                size: norm(draft.size),
                high_low: (norm(draft.high_low) as LocationRow["high_low"]) ?? null,
                temperature,
                sellable: (norm(draft.sellable) as LocationRow["sellable"]) ?? "sellable",
                shelf_drop: Boolean(draft.shelf_drop),
                bay_position: bayPosition,
                created_at: new Date().toISOString(),
            });
            toast.success(t("locations_added_toast", { barcode }), { toastId: `loc-add-ok-${barcode}` });
            cancelEdit();
        } catch {
            toast.error(t("server_error"), { toastId: "loc-add" });
        } finally {
            setSavingBarcode(null);
        }
    };

    const setField = (key: keyof LocationDraft, value: string) => {
        setDraft(prev => prev ? { ...prev, [key]: value } : prev);
    };
    const setBoolField = (key: keyof LocationDraft, value: boolean) => {
        setDraft(prev => prev ? { ...prev, [key]: value } : prev);
    };

    const saveEdit = async () => {
        if (!editingBarcode || !draft) return;
        const original = locations.find(l => l.barcode === editingBarcode);
        if (!original) { cancelEdit(); return; }

        // Build a patch of only the fields that changed. Unlike the bulk
        // upload path (blank = leave unchanged), the popup sends null for
        // a box the admin emptied so the column is actually cleared —
        // PATCH /put-away/locations/:barcode treats present-but-null as
        // "clear". Absent keys are untouched.
        const patch: Record<string, string | number | boolean | null> = {};
        const norm = (s: string): string | null => {
            const v = s.trim();
            return v === "" ? null : v;
        };
        const optionalText = ["zone", "aisle", "bay", "level", "display_name", "size", "high_low", "bay_position"] as const;
        for (const f of optionalText) {
            const next = norm(draft[f]);
            const prev = original[f] ?? null;
            if (next !== prev) patch[f] = next;
        }
        // warehouse / type / sellable are NOT NULL on the DB side, so an
        // emptied box is a validation error rather than a clear.
        const requiredText = [
            ["warehouse", "warehouse_col"],
            ["type", "locations_col_type"],
            ["sellable", "locations_col_sellable"],
        ] as const;
        for (const [f, labelKey] of requiredText) {
            const next = norm(draft[f]);
            if (next === null) {
                toast.error(t("locations_edit_required", { field: t(labelKey) }), { toastId: `loc-edit-${editingBarcode}` });
                return;
            }
            if (next !== (original[f] ?? null)) patch[f] = next;
        }
        if (Boolean(draft.shelf_drop) !== Boolean(original.shelf_drop)) {
            patch.shelf_drop = Boolean(draft.shelf_drop);
        }
        // Barcode rename — the BE re-points stock, totes and history in
        // one transaction and answers 403 for non-super-admins.
        const nextBarcode = draft.barcode.trim();
        if (nextBarcode === "") {
            toast.error(t("locations_edit_required", { field: t("locations_col_barcode") }), { toastId: `loc-edit-${editingBarcode}` });
            return;
        }
        const renaming = nextBarcode !== original.barcode;
        if (renaming) {
            if (!isSuperAdmin) {
                toast.error(t("locations_edit_barcode_superadmin"), { toastId: `loc-edit-${editingBarcode}` });
                return;
            }
            patch.barcode = nextBarcode;
        }
        // Numeric boxes: blank clears; anything non-numeric is ignored
        // (the number input already blocks most junk).
        const numOrNull = (raw: string): number | null | undefined => {
            const s = raw.trim();
            if (s === "") return null;
            const n = Number(s);
            return Number.isFinite(n) ? Math.round(n) : undefined;
        };
        const walkOrder = numOrNull(draft.walk_order);
        if (walkOrder !== undefined && walkOrder !== (original.walk_order ?? null)) {
            patch.walk_order = walkOrder;
        }
        let temperature = numOrNull(draft.temperature);
        if (temperature !== undefined) {
            if (temperature !== null) {
                temperature = Math.max(LOCATION_TEMP_MIN, Math.min(LOCATION_TEMP_MAX, temperature));
            }
            if (temperature !== (original.temperature ?? null)) patch.temperature = temperature;
        }

        if (Object.keys(patch).length === 0) {
            cancelEdit();
            return;
        }

        setSavingBarcode(editingBarcode);
        try {
            const res = await apiFetch(`/put-away/locations/${encodeURIComponent(editingBarcode)}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ...patch, ...readActor() }),
            });
            if (!res.ok) {
                // 400/404 carry a human-readable reason (e.g. "invalid
                // type 'x' — expected …"); surface it verbatim.
                let reason: string | null = null;
                try {
                    const body = await res.json();
                    if (body && typeof body.message === "string") reason = body.message;
                } catch { /* non-JSON error body */ }
                toast.error(reason ?? t("server_error"), { toastId: `loc-edit-${editingBarcode}` });
                return;
            }
            const updated: LocationRow = await res.json();
            onUpdated(editingBarcode, updated);
            toast.success(
                renaming
                    ? t("locations_renamed_toast", { from: editingBarcode, to: updated.barcode })
                    : t("locations_updated_toast", { barcode: editingBarcode }),
                { toastId: `loc-edit-ok-${editingBarcode}` },
            );
            cancelEdit();
        } catch {
            toast.error(t("server_error"), { toastId: `loc-edit-${editingBarcode}` });
        } finally {
            setSavingBarcode(null);
        }
    };

    // Soft delete — flips `deleted_at` server-side. The row vanishes
    // from this list (and every operational query) but stays in the
    // table for audit / restore. Confirmation lives in pendingDelete
    // state; this fires only once the user explicitly confirms.
    const confirmDelete = async () => {
        if (!pendingDelete) return;
        const barcode = pendingDelete.barcode;
        setDeletingBarcode(barcode);
        try {
            const res = await apiFetch(
                `/put-away/locations/${encodeURIComponent(barcode)}`,
                { method: "DELETE" },
            );
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            onDeleted(barcode);
            setPendingDelete(null);
            toast.success(t("locations_deleted_toast", { barcode }), { toastId: `loc-del-${barcode}` });
        } catch {
            toast.error(t("server_error"), { toastId: `loc-del-${barcode}` });
        } finally {
            setDeletingBarcode(null);
        }
    };

    const handleEditKey: React.KeyboardEventHandler = (e) => {
        if (e.key === "Escape") {
            e.preventDefault();
            cancelEdit();
        } else if (e.key === "Enter" && !(e.target instanceof HTMLButtonElement)) {
            e.preventDefault();
            if (adding) saveAdd(); else saveEdit();
        }
    };

    // Big bold word printed beside the barcode. Display name is always
    // preferred — it's the human-readable string admins maintain — and
    // we fall back to the barcode itself so a brand-new row without a
    // display name still produces a sensible label.
    const labelDisplayWord = (l: LocationRow) =>
        (l.display_name && l.display_name.trim()) || l.barcode;

    // Print one location label. Order of preference matches DesksAdmin:
    // saved network printer → local agent (HTML) → browser print dialog.
    // Zebra ZPL barcode-only path isn't used here because the inbound
    // label is A5 landscape with the big-word block — Zebra's print-
    // barcode endpoint only emits a tiny barcode label, not the wall
    // sign layout, so we skip it.
    const printLocationLabel = async (l: LocationRow) => {
        const html = buildLocationLabel(l.barcode, {
            warehouse: l.warehouse ?? "",
            displayWord: labelDisplayWord(l),
            size: labelSize,
        });
        const saved = getSavedNetworkPrinter();
        if (saved) {
            // Only the default A5 size still allows the ZPL fast-path
            // (raw barcode value passed) — anything else needs the HTML
            // render so the chosen page size is honoured rather than
            // being overridden by the printer's hardware label dimensions.
            const rawForZpl = labelSize === "a5" ? l.barcode : undefined;
            const result = await submitNetworkPrintJob(saved.agentId, saved.printerName, html, 1, rawForZpl);
            if (result.success) return { success: true };
        }
        const local = await silentPrint(html);
        if (local.success) return { success: true };
        return browserPrint(html);
    };

    const handlePrint = async (l: LocationRow) => {
        setPrintingBarcode(l.barcode);
        try {
            const result = await printLocationLabel(l);
            if (!result.success) {
                setPrinterNeedsSetup(true);
                toast.error(t("print_failed"), { toastId: `loc-print-${l.barcode}` });
            }
        } finally {
            setPrintingBarcode(null);
        }
    };

    const handleDownload = async (l: LocationRow) => {
        setDownloadingBarcode(l.barcode);
        try {
            const result = await downloadLocationLabelPdf(l.barcode, {
                warehouse: l.warehouse ?? "",
                displayWord: labelDisplayWord(l),
                filename: `location-${l.barcode}.pdf`,
                size: labelSize,
            });
            if (!result.success) {
                toast.error(result.error || t("download_failed"), { toastId: `loc-pdf-${l.barcode}` });
            }
        } finally {
            setDownloadingBarcode(null);
        }
    };

    // Page-based pagination — fixed window, prev/next controls.
    const totalPages = Math.max(1, Math.ceil(filtered.length / LOCATIONS_PAGE_SIZE));
    const safePage = Math.min(page, totalPages - 1);
    const sliceStart = safePage * LOCATIONS_PAGE_SIZE;
    const sliceEnd = sliceStart + LOCATIONS_PAGE_SIZE;
    const visible = filtered.slice(sliceStart, sliceEnd);
    const rangeFrom = filtered.length === 0 ? 0 : sliceStart + 1;
    const rangeTo = Math.min(sliceEnd, filtered.length);

    // Warehouse counts feed the "All N · Newport 12 · Poland 8" pill row
    // — admins see at-a-glance distribution without scrolling the table.
    const warehouseCounts = useMemo(() => {
        const out: Record<string, number> = {};
        for (const l of locations) out[l.warehouse] = (out[l.warehouse] ?? 0) + 1;
        return out;
    }, [locations]);

    return (
        <div className="locations-table-section">
            <div className="dispatch-admin-toolbar">
                <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={startAdd}
                    disabled={draft != null || readOnly}
                    aria-disabled={readOnly}
                    title={readOnly ? t("read_only_mode") : undefined}
                >
                    <Plus size={14} />
                    {t("locations_add_button")}
                </button>
                <button
                    type="button"
                    className="btn btn-ghost btn-sm locations-label-size-btn"
                    onClick={cycleLabelSize}
                    title={t("label_size_aria")}
                    aria-label={t("label_size_aria")}
                >
                    {t("label_size_button", { size: LOCATION_LABEL_DISPLAY[labelSize] })}
                </button>
                <button
                    type="button"
                    className={`btn btn-ghost btn-sm ${printerNeedsSetup ? "dispatch-admin-printer-needed" : ""}`}
                    onClick={() => { setShowPrinterSelect(true); setPrinterNeedsSetup(false); }}
                >
                    <Settings size={14} />
                    {t("printer_select")}
                </button>
                <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => void openRackingPrint()}
                    disabled={draft != null || rackingRows.length === 0 || !rackingWarehouse}
                    title={rackingRows.length > 0 && !rackingWarehouse
                        ? t("locations_print_pick_warehouse")
                        : t("locations_print_labels_aria")}
                    aria-label={t("locations_print_labels_aria")}
                >
                    <Printer size={14} />
                    {t("locations_print_labels", { n: String(rackingRows.length) })}
                </button>
                {hasActiveFilter && (
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={downloadFiltered}
                        disabled={filtered.length === 0}
                    >
                        <Download size={14} /> {t("locations_download_filtered", { n: String(filtered.length) })}
                    </button>
                )}
            </div>

            <FilterPills
                className="warehouse-admin-pills"
                value={warehouseFilter}
                onChange={setWarehouseFilter}
                allCount={locations.length}
                hideAll={!warehouseScope.canCross}
                options={warehouses.map(w => ({ value: w, label: w, count: warehouseCounts[w] }))}
            />
            <div className="warehouse-admin-filters users-admin-filters">
                <input
                    className="locations-table-search wh-admin-filter-search"
                    type="search"
                    placeholder={t("locations_search_hint")}
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                    onKeyDown={e => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            setSearch(searchInput.trim());
                        }
                    }}
                    onBlur={() => {
                        if (searchInput.trim() !== search.trim()) {
                            setSearch(searchInput.trim());
                        }
                    }}
                />
                <select
                    className="locations-table-search wh-admin-filter-select"
                    value={typeFilter}
                    onChange={e => setTypeFilter(e.target.value)}
                >
                    <option value="">{t("locations_filter_all_types")}</option>
                    {types.map(ty => <option key={ty} value={ty}>{ty}</option>)}
                </select>
                <select
                    className="locations-table-search wh-admin-filter-select"
                    value={sizeFilter}
                    onChange={e => setSizeFilter(e.target.value)}
                >
                    <option value="">{t("locations_filter_all_sizes")}</option>
                    {sizeOptions.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <select
                    className="locations-table-search wh-admin-filter-select"
                    value={highLowFilter}
                    onChange={e => setHighLowFilter(e.target.value)}
                >
                    <option value="">{t("locations_filter_all_high_low")}</option>
                    {LOCATION_HIGH_LOW.map(hl => (
                        <option key={hl} value={hl}>{t(`locations_value_${hl}`)}</option>
                    ))}
                </select>
            </div>

            {filtered.length === 0 ? (
                <div className="locations-table-empty">{t("locations_no_matches")}</div>
            ) : (
                <>
                    <div className="locations-table-wrap">
                        <table className="locations-table">
                            <thead>
                                <tr>
                                    <th>{t("locations_col_barcode")}</th>
                                    <th>{t("locations_col_name")}</th>
                                    <th>{t("warehouse_col")}</th>
                                    <th>{t("locations_col_type")}</th>
                                    <th>{t("locations_col_zone")}</th>
                                    <th>{t("locations_col_aisle")}</th>
                                    <th>{t("locations_col_bay")}</th>
                                    <th>{t("locations_col_level")}</th>
                                    <th>{t("locations_col_walk_order")}</th>
                                    <th>{t("locations_col_size")}</th>
                                    <th>{t("locations_col_high_low")}</th>
                                    <th>{t("locations_col_temperature")}</th>
                                    <th>{t("locations_col_sellable")}</th>
                                    <th>{t("locations_col_shelf_drop")}</th>
                                    <th aria-hidden="true" />
                                </tr>
                            </thead>
                            <tbody>
                                {visible.map(l => {
                                    return (
                                        <tr
                                            key={l.barcode}
                                            className="locations-table-row--clickable"
                                            onClick={() => startEdit(l)}
                                            title={t("locations_row_click_hint")}
                                        >
                                            <td className="locations-table-mono" title={l.barcode}>{truncateCell(l.barcode)}</td>
                                            <td title={l.display_name ?? ""}>{truncateCell(l.display_name ?? "")}</td>
                                            <td>{l.warehouse}</td>
                                            <td>{l.type ?? ""}</td>
                                            <td>{l.zone ?? ""}</td>
                                            <td>{l.aisle ?? ""}</td>
                                            <td>{l.bay ?? ""}</td>
                                            <td>{l.level ?? ""}</td>
                                            <td className="locations-table-num">{l.walk_order ?? ""}</td>
                                            <td>{l.size ?? ""}</td>
                                            <td>{l.high_low ? t(`locations_value_${l.high_low}`) : ""}</td>
                                            <td className="locations-table-num">
                                                {l.temperature != null
                                                    ? `${l.temperature <= 3 ? "🥶 " : l.temperature >= 8 ? "🔥 " : ""}${l.temperature}`
                                                    : ""}
                                            </td>
                                            <td>{t(`locations_value_${l.sellable ?? "sellable"}`)}</td>
                                            <td>{l.shelf_drop ? t("yes") : t("no")}</td>
                                            <td className="locations-table-actions" onClick={e => e.stopPropagation()}>
                                                <button
                                                    type="button"
                                                    className="locations-table-btn"
                                                    onClick={() => startEdit(l)}
                                                    disabled={editingBarcode != null || readOnly}
                                                    aria-disabled={readOnly}
                                                    aria-label={t("edit")}
                                                    title={readOnly ? t("read_only_mode") : t("edit")}
                                                >
                                                    <Pencil size={14} />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="locations-table-btn"
                                                    onClick={() => handlePrint(l)}
                                                    disabled={editingBarcode != null || printingBarcode === l.barcode}
                                                    aria-label={t("print_label")}
                                                    title={t("print_label")}
                                                >
                                                    {printingBarcode === l.barcode
                                                        ? <Loader2 size={14} className="dispatch-admin-spin" />
                                                        : <Printer size={14} />}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="locations-table-btn"
                                                    onClick={() => handleDownload(l)}
                                                    disabled={editingBarcode != null || downloadingBarcode === l.barcode}
                                                    aria-label={t("download")}
                                                    title={t("download")}
                                                >
                                                    {downloadingBarcode === l.barcode
                                                        ? <Loader2 size={14} className="dispatch-admin-spin" />
                                                        : <Download size={14} />}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="locations-table-btn locations-table-btn--delete"
                                                    onClick={() => setPendingDelete(l)}
                                                    disabled={editingBarcode != null || readOnly}
                                                    aria-disabled={readOnly}
                                                    aria-label={t("delete")}
                                                    title={readOnly ? t("read_only_mode") : t("delete")}
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div className="locations-table-footer">
                        <span>{t("locations_showing_range", {
                            from: String(rangeFrom),
                            to: String(rangeTo),
                            total: String(filtered.length),
                        })}</span>
                        <div className="locations-table-pagination">
                            <button
                                type="button"
                                className="locations-table-page-btn"
                                onClick={() => setPage(p => Math.max(0, p - 1))}
                                disabled={safePage === 0}
                                aria-label={t("pagination_previous")}
                            >
                                <ChevronLeft size={16} />
                                <span>{t("pagination_previous")}</span>
                            </button>
                            <span className="locations-table-page-indicator">
                                {t("pagination_page_x_of_y", {
                                    page: String(safePage + 1),
                                    total: String(totalPages),
                                })}
                            </span>
                            <button
                                type="button"
                                className="locations-table-page-btn"
                                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                                disabled={safePage >= totalPages - 1}
                                aria-label={t("pagination_next")}
                            >
                                <span>{t("pagination_next")}</span>
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                </>
            )}

            {(adding || editingBarcode != null) && draft && (() => {
                const isSaving = savingBarcode === (adding ? ADD_SAVING_KEY : editingBarcode);
                const barcodeLocked = !adding && !isSuperAdmin;
                const textField = (key: "zone" | "aisle" | "bay" | "level", labelKey: string) => (
                    <label className="locations-edit-field">
                        <span>{t(labelKey)}</span>
                        <input
                            className="locations-table-input"
                            value={draft[key]}
                            onChange={e => setField(key, e.target.value)}
                            disabled={isSaving}
                        />
                    </label>
                );
                return (
                    <div
                        className="modal-overlay"
                        onClick={() => !isSaving && cancelEdit()}
                    >
                        <div
                            className="modal-card locations-edit-modal"
                            onClick={e => e.stopPropagation()}
                            onKeyDown={handleEditKey}
                            role="dialog"
                            aria-labelledby="locations-edit-title"
                        >
                            <div className="modal-card-header">
                                <h2 id="locations-edit-title">{t(adding ? "locations_add_title" : "locations_edit_title")}</h2>
                                <button
                                    type="button"
                                    className="modal-close"
                                    onClick={cancelEdit}
                                    disabled={isSaving}
                                    aria-label={t("close")}
                                >
                                    <X size={18} />
                                </button>
                            </div>
                            <div className="modal-card-body">
                                <div className="locations-edit-grid">
                                    <label className="locations-edit-field locations-edit-field--wide">
                                        <span>{t("locations_col_barcode")}</span>
                                        <input
                                            className="locations-table-input locations-table-mono"
                                            value={draft.barcode}
                                            onChange={e => setField("barcode", e.target.value)}
                                            readOnly={barcodeLocked}
                                            disabled={isSaving || barcodeLocked}
                                            title={barcodeLocked ? t("locations_edit_barcode_superadmin") : undefined}
                                            placeholder={adding ? t("locations_add_barcode_placeholder") : undefined}
                                            autoFocus={adding}
                                        />
                                        {!adding && isSuperAdmin && draft.barcode.trim() !== "" && draft.barcode.trim() !== editingBarcode && (
                                            <span className="locations-edit-warning">
                                                {t("locations_edit_barcode_rename_warning")}
                                            </span>
                                        )}
                                    </label>
                                    <label className="locations-edit-field locations-edit-field--wide">
                                        <span>{t("locations_col_name")}</span>
                                        <input
                                            className="locations-table-input"
                                            value={draft.display_name}
                                            onChange={e => setField("display_name", e.target.value)}
                                            disabled={isSaving}
                                            autoFocus={!adding}
                                        />
                                    </label>
                                    <label className="locations-edit-field">
                                        <span>{t("warehouse_col")}</span>
                                        {adding ? (
                                            <>
                                                <input
                                                    className="locations-table-input"
                                                    list="locations-add-warehouses"
                                                    value={draft.warehouse}
                                                    onChange={e => setField("warehouse", e.target.value)}
                                                    disabled={isSaving}
                                                />
                                                <datalist id="locations-add-warehouses">
                                                    {warehouses.map(w => <option key={w} value={w} />)}
                                                </datalist>
                                            </>
                                        ) : (
                                        <select
                                            className="locations-table-input"
                                            value={draft.warehouse}
                                            onChange={e => setField("warehouse", e.target.value)}
                                            disabled={isSaving}
                                        >
                                            {/* Show the current value even if it's no longer in the
                                                warehouses list — avoids silently re-mapping it. */}
                                            {!warehouses.includes(draft.warehouse) && draft.warehouse !== "" && (
                                                <option value={draft.warehouse}>{draft.warehouse}</option>
                                            )}
                                            {warehouses.map(w => <option key={w} value={w}>{w}</option>)}
                                        </select>
                                        )}
                                    </label>
                                    <label className="locations-edit-field">
                                        <span>{t("locations_col_type")}</span>
                                        <select
                                            className="locations-table-input"
                                            value={draft.type}
                                            onChange={e => setField("type", e.target.value)}
                                            disabled={isSaving}
                                        >
                                            {/* Show the current value even if it's no longer in the
                                                catalogue — avoids silently re-mapping it. */}
                                            {draft.type && !typeOptions.includes(draft.type) && (
                                                <option value={draft.type}>{draft.type}</option>
                                            )}
                                            {typeOptions.map(ty => <option key={ty} value={ty}>{ty}</option>)}
                                        </select>
                                    </label>
                                    {textField("zone", "locations_col_zone")}
                                    {textField("aisle", "locations_col_aisle")}
                                    {textField("bay", "locations_col_bay")}
                                    {textField("level", "locations_col_level")}
                                    <label className="locations-edit-field">
                                        <span>{t("locations_col_walk_order")}</span>
                                        <input
                                            className="locations-table-input locations-table-input--num"
                                            type="number"
                                            value={draft.walk_order}
                                            onChange={e => setField("walk_order", e.target.value)}
                                            disabled={isSaving}
                                        />
                                    </label>
                                    <label className="locations-edit-field">
                                        <span>{t("locations_col_size")}</span>
                                        <select
                                            className="locations-table-input"
                                            value={draft.size}
                                            onChange={e => setField("size", e.target.value)}
                                            disabled={isSaving}
                                        >
                                            <option value="">—</option>
                                            {/* Show the current value even if it's no longer in the
                                                catalogue — avoids silently re-mapping it. */}
                                            {draft.size && !sizeOptions.includes(draft.size) && (
                                                <option value={draft.size}>{draft.size}</option>
                                            )}
                                            {sizeOptions.map(s => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </label>
                                    <label className="locations-edit-field">
                                        <span>{t("locations_col_high_low")}</span>
                                        <select
                                            className="locations-table-input"
                                            value={draft.high_low}
                                            onChange={e => setField("high_low", e.target.value)}
                                            disabled={isSaving}
                                        >
                                            <option value="">—</option>
                                            {LOCATION_HIGH_LOW.map(hl => (
                                                <option key={hl} value={hl}>{t(`locations_value_${hl}`)}</option>
                                            ))}
                                        </select>
                                    </label>
                                    <label className="locations-edit-field">
                                        <span>{t("locations_col_temperature")}</span>
                                        <input
                                            className="locations-table-input locations-table-input--num"
                                            type="number"
                                            min={LOCATION_TEMP_MIN}
                                            max={LOCATION_TEMP_MAX}
                                            step={1}
                                            value={draft.temperature}
                                            onChange={e => setField("temperature", e.target.value)}
                                            disabled={isSaving}
                                            placeholder="1–10"
                                        />
                                    </label>
                                    <label className="locations-edit-field">
                                        <span>{t("locations_col_bay_position")}</span>
                                        <select
                                            className="locations-table-input"
                                            value={draft.bay_position}
                                            onChange={e => setField("bay_position", e.target.value)}
                                            disabled={isSaving}
                                        >
                                            <option value="">{t("locations_bay_position_none")}</option>
                                            {LOCATION_BAY_POSITIONS.map(bp => (
                                                <option key={bp} value={bp}>{bp}</option>
                                            ))}
                                        </select>
                                    </label>
                                    <label className="locations-edit-field">
                                        <span>{t("locations_col_sellable")}</span>
                                        <select
                                            className="locations-table-input"
                                            value={draft.sellable}
                                            onChange={e => setField("sellable", e.target.value)}
                                            disabled={isSaving}
                                        >
                                            {LOCATION_SELLABLE.map(s => (
                                                <option key={s} value={s}>{t(`locations_value_${s}`)}</option>
                                            ))}
                                        </select>
                                    </label>
                                    <label className="locations-edit-field locations-edit-field--toggle">
                                        <span>{t("locations_col_shelf_drop")}</span>
                                        <span className="admin-settings-toggle">
                                            <input
                                                type="checkbox"
                                                checked={!!draft.shelf_drop}
                                                onChange={e => setBoolField("shelf_drop", e.target.checked)}
                                                disabled={isSaving}
                                            />
                                            <span className="admin-settings-slider" />
                                        </span>
                                    </label>
                                </div>
                                <p className="locations-edit-hint">
                                    {adding ? t("locations_add_hint") : (
                                        <>
                                            {t("locations_edit_hint")}
                                            {!isSuperAdmin && <> {t("locations_edit_barcode_superadmin")}</>}
                                        </>
                                    )}
                                </p>
                                <div className="modal-confirm-buttons">
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-sm"
                                        onClick={cancelEdit}
                                        disabled={isSaving}
                                    >
                                        {t("cancel")}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary btn-sm"
                                        onClick={adding ? saveAdd : saveEdit}
                                        disabled={isSaving || readOnly}
                                        aria-disabled={readOnly}
                                        title={readOnly ? t("read_only_mode") : undefined}
                                    >
                                        {isSaving ? <Loader2 size={14} className="dispatch-admin-spin" /> : <Check size={14} />}
                                        {isSaving ? t("saving") : t(adding ? "locations_add_submit" : "save")}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {pendingDelete && (
                <div
                    className="modal-overlay"
                    onClick={() => deletingBarcode == null && setPendingDelete(null)}
                >
                    <div
                        className="modal-card locations-delete-modal"
                        onClick={e => e.stopPropagation()}
                        role="alertdialog"
                        aria-labelledby="locations-delete-title"
                    >
                        <div className="modal-card-header">
                            <h2 id="locations-delete-title">{t("locations_delete_title")}</h2>
                            <button
                                type="button"
                                className="modal-close"
                                onClick={() => setPendingDelete(null)}
                                disabled={deletingBarcode != null}
                                aria-label={t("close")}
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <p className="locations-delete-body">
                                {t("locations_delete_body", { barcode: pendingDelete.barcode })}
                            </p>
                            <p className="locations-delete-meta">
                                <span className="locations-delete-meta-label">{t("warehouse_col")}:</span>
                                <span>{pendingDelete.warehouse}</span>
                                {pendingDelete.display_name && (
                                    <>
                                        <span className="locations-delete-meta-sep">·</span>
                                        <span>{pendingDelete.display_name}</span>
                                    </>
                                )}
                            </p>
                            <p className="locations-delete-hint">{t("locations_delete_hint")}</p>
                            <div className="modal-confirm-buttons">
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-sm"
                                    onClick={() => setPendingDelete(null)}
                                    disabled={deletingBarcode != null}
                                >
                                    {t("cancel")}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger btn-sm"
                                    onClick={confirmDelete}
                                    disabled={deletingBarcode != null || readOnly}
                                    aria-disabled={readOnly}
                                    title={readOnly ? t("read_only_mode") : undefined}
                                >
                                    <Trash2 size={14} />
                                    {deletingBarcode != null ? t("deleting") : t("delete")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {printRacking && rackingWarehouse && (
                <SortingLabelPrintModal
                    warehouse={rackingWarehouse}
                    title={t("locations_print_labels_title", { warehouse: rackingWarehouse })}
                    hint={t("locations_print_labels_hint", { count: String(rackingRows.length), warehouse: rackingWarehouse })}
                    showRange={false}
                    selectable
                    defaultSize={RACKING_LABEL_BASE}
                    styles={["racking", "standard", "compact", "large", "large152", "band6x4"]}
                    groups={LOCATION_HIGH_LOW.map(hl => ({ key: hl, label: t(`locations_value_${hl}`) }))}
                    skipLevels={RACKING_SKIP_LEVELS}
                    labels={rackingRows.map(l => rackingLabel(l, rackingPalette))}
                    widthMm={rackingSize.w}
                    heightMm={rackingSize.h}
                    onSaveSize={saveRackingSize}
                    onClose={() => setPrintRacking(false)}
                />
            )}

            {showPrinterSelect && (
                <PrinterSelectModal
                    autoPrint={true}
                    printMode={null}
                    onSelect={() => setShowPrinterSelect(false)}
                    onConnectLocal={() => setShowPrinterSelect(false)}
                    onDisconnect={() => setShowPrinterSelect(false)}
                    onClose={() => setShowPrinterSelect(false)}
                />
            )}
        </div>
    );
};

// Compact single-row KPI strip. Shows total locations + a per-warehouse
// pill row. The zone breakdown that used to live here was dropped — it
// expanded the strip vertically without paying for that space, and the
// table's filters cover the same need.
const LocationsSummary = ({ locations }: { locations: LocationRow[] }) => {
    const { t } = useTranslation();
    const byWarehouse: Record<string, number> = {};
    for (const l of locations) {
        byWarehouse[l.warehouse] = (byWarehouse[l.warehouse] ?? 0) + 1;
    }
    const warehouses = Object.keys(byWarehouse).sort();

    return (
        <div className="locations-summary">
            <div className="locations-summary-total">
                <span className="locations-summary-total-label">{t("locations_total_label")}</span>
                <strong>{locations.length}</strong>
            </div>
            <div className="locations-summary-warehouses">
                {warehouses.map(wh => (
                    <div key={wh} className="locations-summary-pill">
                        <span className="locations-summary-pill-label">{wh}</span>
                        <span className="locations-summary-pill-value">{byWarehouse[wh]}</span>
                    </div>
                ))}
                {warehouses.length === 0 && (
                    <span className="locations-summary-empty">{t("no_warehouses")}</span>
                )}
            </div>
        </div>
    );
};

export default LocationsAdmin;
